//Libs
import { Fragment, useEffect, useRef, useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";

//Local
import VehicleListPage from "./VehicleListPage";
import {
  ExistingVehicle,
  Vehicle_CreateAPI,
  VehicleManager,
} from "../../models/vehicle";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { logger } from "../../logging";
import { DbRead, DbWrite } from "../../database";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import * as strings from "../../strings";
import AddEditVehicleDialog, {
  VehicleFormState,
} from "../../components/Vehicles/AddEditVehicleDialog";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { IMultipleUid_AssignUser } from "../../components/CustomFields/MultipleUidDialog";
import { useSiteKeyUsersStore } from "../../store/site-key-users";
import { useAuthStore } from "../../store/firebase-auth";
import { User } from "firebase/auth";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { DocumentData } from "firebase/firestore";
import { diffObjects } from "../../assets/js/object-diff";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { isDefinedCraftTypes } from "../Admin/CompaniesListContainer";
import { isDefinedTaskTypes } from "../../models/task-types";
import { parseSmartSchedulingFormValues } from "../../models/smart-scheduling-options";
import { SmartSchedulingSettingsDialog } from "../../components/SmartSchedulingSettingsDialog";

interface Props {
  siteKey: string;
}

export default function VehicleListContainer({ siteKey }: Props) {
  // #region SECTION: Stores
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const [getLocationTitle, locationStoreIsLoading, siteKeyLocationList] =
    useSiteKeyLocationsStore((state) => [
      state.getLocationTitle,
      state.loading,
      state.siteKeyLocationList,
    ]);
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );
  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);
  const siteKeyUsersList = useSiteKeyUsersStore(
    (state) => state.siteKeyUsersList,
  );
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);
  // #endregion SECTION: Stores

  // #region SECTION: useState, useEffect

  const [vehicleList, setVehicleList] = useState<ExistingVehicle[]>([]);
  const [vehicleListIsLoading, setVehicleListIsLoading] =
    useState<boolean>(false);

  // Fetch the list of vehicles when this component loads.
  useEffect(() => {
    async function getVehicles() {
      logger.debug("getVehicles useEffect called ");
      // Query vehicles via realtime updates. Set the list of vehicles.
      const unsubscribeAllVehicles = DbRead.vehicles.subscribeAll({
        siteKey: siteKey,
        onChange: (vehicleList: ExistingVehicle[]) => {
          setVehicleList(vehicleList), setVehicleListIsLoading(false);
        },
        onError: (error) =>
          logger.error(`Error in getVehicles: ${error.message}`),
      });
      return unsubscribeAllVehicles;
    }

    // Store the returned 'unsubscribe' ƒn into the unsubscribePromise variable.
    setVehicleListIsLoading(true);
    const unsubscribePromise = getVehicles();
    // Return an anonymous ƒn for cleanup.
    return () => {
      logger.debug(
        "(VehicleListContainer) getVehicles useEffect return ƒn just invoked.",
      );
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) {
          logger.debug("Now unsubscribing from vehicles.");
          unsubscribe();
        }
      });
      logger.debug("Done running cleanup ƒn for getVehicles useEffect.");
    };
  }, [siteKey]);

  const [addEditVehicleDialogOpen, setAddEditVehicleDialogOpen] =
    useState<boolean>(false);
  const [selectedVehicle, setSelectedVehicle] =
    useState<ExistingVehicle | null>(null);

  const userList = useRef<IMultipleUid_AssignUser[]>([]);

  useEffect(() => {
    async function getUserList() {
      userList.current = siteKeyUsersList
        // we don't want system users to be selectable in the multiple-uid dialog
        .filter((user) => user.systemUser !== true)
        .map((user) => ({
          uid: user.id,
          name: user.displayName,
          company: user.companyName,
          avatarUrl: user.userPhoto_URL,
          isAssigned: false,
          assignedVehicle: null,
        }));
    }

    getUserList();
  }, [siteKeyUsersList]);

  // #endregion SECTION: useState, useEffect

  // #region SECTION: Functions

  async function handleEditVehicle(updateVehicle: VehicleFormState) {
    if (firebaseUser == null) {
      logger.error("firebaseUser is null");
      return;
    }
    if (!selectedVehicle) return;

    const selectedSiteKeyLocation = siteKeyLocationList.find(
      (siteLocation) => siteLocation.id === updateVehicle.locationID,
    );

    const selectedUsers = updateVehicle.defaultSiteKeyUserID.filter(
      (userID): userID is string => userID != null,
    );

    // Drop undefined values from the craftTypes list.
    const definedCraftTypes =
      updateVehicle.craftTypes.filter(isDefinedCraftTypes);
    // Drop undefined values from the craftTypes list.
    const definedTaskTypes = updateVehicle.taskTypes.filter(isDefinedTaskTypes);

    const partialUpdateVechicle: Partial<ExistingVehicle> = {
      ...updateVehicle,
      defaultSiteKeyUserID: selectedUsers,
      latitude: selectedSiteKeyLocation?.latitude ?? 0,
      longitude: selectedSiteKeyLocation?.longitude ?? 0,
      craftTypes: definedCraftTypes,
      taskTypes: definedTaskTypes,
    };

    const diffVehicleValues: DocumentData = diffObjects(
      selectedVehicle,
      partialUpdateVechicle,
    ).diff;

    if (Object.keys(diffVehicleValues).length === 0) {
      logger.debug("No values have changed");
      return;
    }

    diffVehicleValues["id"] = selectedVehicle.id;
    diffVehicleValues["refPath"] = selectedVehicle.refPath;
    diffVehicleValues["lastModifiedBy"] = firebaseUser.uid;

    //Validate
    const validatedEditVehicle = VehicleManager.parseUpdate(diffVehicleValues);
    logger.info("Validated Vehicle:", validatedEditVehicle);

    //DB
    try {
      await DbWrite.vehicles.update(validatedEditVehicle);
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulUpdate(selectedVehicle.title),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleEditVehicle`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  async function handleSaveNewVehicle(formValues: VehicleFormState) {
    if (firebaseUser == null) {
      logger.error("firebaseUser is null");
      return;
    }

    const selectedSiteKeyLocation = siteKeyLocationList.find(
      (siteLocation) => siteLocation.id === formValues.locationID,
    );

    const selectedUsers = formValues.defaultSiteKeyUserID.filter(
      (userID): userID is string => userID != null,
    );

    const definedCraftTypes = formValues.craftTypes.filter(
      (craftType) => craftType != undefined,
    );
    const definedTaskTypes = formValues.taskTypes.filter(
      (taskType) => taskType != undefined,
    );

    const newVehicle: Vehicle_CreateAPI = {
      ...formValues,
      defaultSiteKeyUserID: selectedUsers,
      latitude: selectedSiteKeyLocation?.latitude ?? 0,
      longitude: selectedSiteKeyLocation?.longitude ?? 0,
      assetID: undefined,
      customID: null,
      thumbnailURL: null,
      qrCode: null,
      deleted: false,
      createdBy: firebaseUser.uid,
      lastModifiedBy: firebaseUser.uid,
      siteKey: siteKey,
      craftTypes: definedCraftTypes,
      taskTypes: definedTaskTypes,
    };

    //Validate
    const validatedVehicle = VehicleManager.parseCreate(newVehicle);
    logger.info("Validated Vehicle:", validatedVehicle);

    //DB
    try {
      await DbWrite.vehicles.create(validatedVehicle);
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulAdd(validatedVehicle.title),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleSaveNewVehicle`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  async function handleDeleteVehicle(vehicleID: ExistingVehicle["id"]) {
    if (!selectedVehicle) return;

    const title = selectedVehicle.title;

    try {
      logger.debug(`Deleting vehicle with ID: ${vehicleID}`);

      await DbWrite.vehicles.delete(siteKey, vehicleID);
      setAddEditVehicleDialogOpen(false);
      setSelectedVehicle(null);
      logger.debug("Vehicle deleted successfully.");
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulDelete(title),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleDeleteVehicle`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  function openEditVehicleDialog(vehicle: ExistingVehicle) {
    setAddEditVehicleDialogOpen(true);
    setSelectedVehicle(vehicle);
  }

  // Add state for smart scheduling dialog
  const [smartSchedulingDialogOpen, setSmartSchedulingDialogOpen] =
    useState(false);

  // #endregion SECTION: Functions

  // #region SECTION: Components

  const addNewVehicleButton = (
    <BaseButtonPrimary
      type="button"
      onClick={() => setAddEditVehicleDialogOpen(true)}
      className="w-full text-primary sm:w-48"
    >
      <AddCircleIcon fontSize="small" className="mr-2" />
      {strings.ADD_NEW_VEHICLE}
    </BaseButtonPrimary>
  );

  const addEditVehicleDialog = userPermissions && siteKeyDoc && (
    <AddEditVehicleDialog
      isDialogOpen={addEditVehicleDialogOpen}
      closeDialog={() => {
        setAddEditVehicleDialogOpen(false);
        setSelectedVehicle(null);
      }}
      handleEditVehicle={handleEditVehicle}
      handleSaveNewVehicle={handleSaveNewVehicle}
      handleDeleteVehicle={handleDeleteVehicle}
      siteKeyLocationList={siteKeyLocationList}
      userPermissions={userPermissions}
      vehicleDoc={selectedVehicle}
      userList={userList.current}
      siteKeyDoc={siteKeyDoc}
    />
  );

  // #endregion SECTION: Components

  /* RENDER LOADING */
  if (
    vehicleListIsLoading ||
    locationStoreIsLoading ||
    userDisplayNamesMapIsLoading ||
    siteKeyDocIsLoading
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  return (
    <Fragment>
      <VehicleListPage
        vehicleList={vehicleList.sort((a, b) => a.title.localeCompare(b.title))}
        getLocationTitle={getLocationTitle}
        addNewVehicleButton={addNewVehicleButton}
        onEditVehicle={openEditVehicleDialog}
        userDisplayNamesMap={userDisplayNamesMap}
        onOpenSmartScheduling={() => setSmartSchedulingDialogOpen(true)}
      />
      {addEditVehicleDialog}
      <SmartSchedulingSettingsDialog
        open={smartSchedulingDialogOpen}
        onClose={() => setSmartSchedulingDialogOpen(false)}
        initialValues={parseSmartSchedulingFormValues(
          siteKeyDoc?.customizations.smartSchedulingSettings,
        )}
        siteKey={siteKey}
        validTaskTypes={siteKeyDoc?.validTaskTypes ?? []}
      />
    </Fragment>
  );
}
